/**
 * Body Ballancer runs at four sites (from 2026-10-12):
 *
 *   Monday                         Manchester — our room at the Royal Exchange, 11–4
 *   4th Wednesday of each month    Yoga on the Edge (YOTE), Alderley Edge, 11–4 (from 28 Oct 2026)
 *   Thursday – Saturday            Stocks House
 *   every other day                Nicola's clinic (Tuesday 11–4)
 *
 * The YOTE day is the one entry not decided by weekday alone, so the rota is a
 * weekly table plus an override for that day — see `getLocationIdForDate`.
 *
 * This file is the ONLY place that rota lives — the same discipline as
 * `app/dayclinic/clinicDates.ts`, which is the only list of IV clinic days.
 * Everything that needs to know where a session happens derives it from here.
 *
 * ── Location is derived when OFFERING a slot, and stamped when WRITING one ──
 *
 * Because the split is by day of week, location is a pure function of the date,
 * so there is no capacity to split and no second calendar: one machine, one
 * Nicola, and the day partition guarantees she is never double-booked across
 * sites. The whole overlap machinery (computeDaySlots → isSlotBookable →
 * body_ballancer_no_overlap) is unaffected.
 *
 * But the derivation must NOT be repeated at read time. `body_ballancer_bookings.location`
 * is stamped at booking time and read everywhere after, because otherwise:
 *
 *   1. Rescheduling teleports people. `/api/body-ballancer/admin/reschedule` moves a
 *      booking to any date; a Thursday moved to a Tuesday would silently change site.
 *   2. Changing this rota rewrites history — swap Wednesday to Stocks House next year
 *      and every past Wednesday booking retroactively claims it was there.
 *   3. The confirmation email is the customer's record of where to go. The row must
 *      agree with what was sent.
 *
 * Stamping is also what grandfathers the cut-over: every row that existed before the
 * split was backfilled to `stockshouse` (drizzle/0023), which was true at the time, so
 * the rota only ever applies to bookings made after it. No effective-from date needed.
 *
 * ── Client-safe ──
 *
 * This module is a plain module with no `server-only`, so client components can import
 * the rota to render the location picker. Street addresses are deliberately NOT in it:
 * Nicola's site is private (`publicAddress: false`) and is only ever disclosed in the
 * confirmation email and calendar invite. Addresses come from the environment via
 * `getLocationAddress`, which returns "" in a browser bundle because Next.js only
 * inlines NEXT_PUBLIC_* vars — so an address cannot leak into client JS by accident.
 *
 * The sites are 20–40 miles apart (Preston, Southport, Manchester, Alderley Edge), which
 * is why the booking flow asks for a location BEFORE showing the calendar rather than
 * letting someone pick a time and find out where it is afterwards.
 */

export type BallancerLocationId = "nicola" | "stockshouse" | "manchester" | "yote";

export interface BallancerLocation {
  id: BallancerLocationId;
  /** Shown everywhere, including publicly. Never the street address. */
  name: string;
  /** Rough area, safe to show before booking, so someone can judge the journey. */
  area: string;
  /** The one town name to use in running copy ("Preston (Sunday, …)"). */
  town: string;
  /**
   * False when the street address must not appear on the public site, in JSON-LD or
   * in the calendar grid — only in the confirmation email and the calendar invite.
   */
  publicAddress: boolean;
  /**
   * Env var holding the full street address. Read server-side only.
   *
   * Nicola's site deliberately reuses `DAYCLINIC_ADDRESS` rather than having a var of its
   * own — it is the same physical building the IV day clinic runs from, and two vars for
   * one address is two things to keep in step and one of them to get wrong. If the clinic
   * ever moves, Body Ballancer's Sun–Wed address should move with it, which is exactly
   * what sharing the var gives us. Don't "fix" this by splitting them.
   */
  addressEnvVar: string;
  /** Optional extra shown in confirmation emails (parking, entrance, what to bring). */
  arrivalNotes?: string;
  /**
   * Fixed session window at this site, "HH:MM-HH:MM". Wins over the weekday's
   * BALLANCER_HOURS_<DOW>: the hired rooms are booked for a set window whatever
   * day they fall on, and a YOTE Wednesday must not inherit Wednesday's 9–8 at
   * Nicola's. Sites without one use the weekday hours (app/lib/ballancerSchedule.ts).
   */
  hours?: string;
  /**
   * Also put each session in Nicola's own diary (CALENDAR_USER), not just the Body
   * Ballancer mailbox. Set for the hired rooms: a day there means travelling, so it
   * has to show where she plans her week. See app/lib/ballancerNicolaDiary.ts.
   */
  mirrorToNicolaDiary?: boolean;
}

export const LOCATIONS: Record<BallancerLocationId, BallancerLocation> = {
  nicola: {
    id: "nicola",
    town: "Preston",
    name: "Nicola's clinic",
    area: "New Longton, Preston",
    publicAddress: false,
    // Same building as the IV day clinic — see addressEnvVar above.
    addressEnvVar: "DAYCLINIC_ADDRESS",
    arrivalNotes: "Please don't arrive more than 5 minutes early — there is no waiting area.",
  },
  stockshouse: {
    id: "stockshouse",
    town: "Southport",
    name: "Stocks House",
    area: "Scarisbrick, Southport",
    publicAddress: true,
    addressEnvVar: "BALLANCER_ADDRESS_STOCKSHOUSE",
  },
  manchester: {
    id: "manchester",
    town: "Manchester",
    name: "Manchester Royal Exchange",
    area: "St Ann's Square, Manchester",
    publicAddress: true,
    addressEnvVar: "BALLANCER_ADDRESS_MANCHESTER",
    arrivalNotes: "We are in Room 436 on the fourth floor of the Royal Exchange building — take the lift from the St Ann's Square entrance.",
    hours: "11:00-16:00",
    mirrorToNicolaDiary: true,
  },
  yote: {
    id: "yote",
    town: "Alderley Edge",
    name: "Yoga on the Edge",
    area: "Alderley Edge, Cheshire",
    publicAddress: true,
    addressEnvVar: "BALLANCER_ADDRESS_YOTE",
    hours: "11:00-16:00",
    mirrorToNicolaDiary: true,
  },
};

export const LOCATION_LIST: BallancerLocation[] = [
  LOCATIONS.nicola,
  LOCATIONS.manchester,
  LOCATIONS.yote,
  LOCATIONS.stockshouse,
];

/**
 * The rota, indexed by JavaScript day-of-week (0 = Sunday … 6 = Saturday).
 * `null` would mean "no site that day" — the day is then closed regardless of
 * whatever BALLANCER_HOURS_* says, so a stray hours var can't open a day with
 * nowhere to hold it.
 */
export const ROTA: readonly (BallancerLocationId | null)[] = [
  "nicola",      // Sun
  "manchester",  // Mon
  "nicola",      // Tue
  "nicola",      // Wed
  "stockshouse", // Thu
  "stockshouse", // Fri
  "stockshouse", // Sat
];

/**
 * Every booking taken before the two-site split happened at Stocks House, which was
 * the only location. drizzle/0023 backfills existing rows to this, and it is the
 * fallback when a row somehow has no location stamped.
 */
export const LEGACY_LOCATION_ID: BallancerLocationId = "stockshouse";

/**
 * Day of week for an ISO date, computed in UTC so it can never be shifted by the
 * server's local timezone. Booking dates are plain "YYYY-MM-DD" calendar dates with
 * no instant attached, so UTC is the right frame — `new Date("2026-09-10")` parsed
 * locally and then read with getDay() is exactly how an off-by-one rota bug happens.
 */
function dayOfWeek(isoDate: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return null;
  const [, y, mo, d] = m;
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  if (Number.isNaN(dt.getTime())) return null;
  // Guard against rollover from an invalid date like 2026-02-31.
  if (dt.getUTCMonth() !== Number(mo) - 1 || dt.getUTCDate() !== Number(d)) return null;
  return dt.getUTCDay();
}

/**
 * The monthly YOTE day: the 4th Wednesday of the month, from 28 October 2026.
 * It displaces whatever the weekly rota says for that Wednesday (Nicola's).
 *
 * A rule rather than a list of dates so the calendar never runs out. If YOTE
 * dates start moving around, swap this for an explicit list like
 * app/dayclinic/clinicDates.ts — it is the only place that needs to change.
 */
export const YOTE_FROM = "2026-10-28";
const YOTE_WEEKDAY = 3; // Wednesday
const YOTE_NTH = 4;

export function isYoteDate(isoDate: string): boolean {
  if (isoDate < YOTE_FROM) return false;
  const dow = dayOfWeek(isoDate);
  if (dow !== YOTE_WEEKDAY) return false;
  const dom = Number(isoDate.slice(8, 10));
  return Math.ceil(dom / 7) === YOTE_NTH;
}

/** Which site runs on this date, or null if none does. */
export function getLocationIdForDate(isoDate: string): BallancerLocationId | null {
  const dow = dayOfWeek(isoDate);
  if (dow === null) return null;
  if (isYoteDate(isoDate)) return "yote";
  return ROTA[dow] ?? null;
}

/** Full location record for a date, or null if no site runs that day. */
export function getLocationForDate(isoDate: string): BallancerLocation | null {
  const id = getLocationIdForDate(isoDate);
  return id ? LOCATIONS[id] : null;
}

/**
 * Resolve a stored location value to a record, tolerating NULL and unknown strings.
 * Read paths use this so a legacy or hand-edited row renders an address rather than
 * blowing up in a confirmation email.
 */
export function resolveLocation(id: string | null | undefined): BallancerLocation {
  if (id && id in LOCATIONS) return LOCATIONS[id as BallancerLocationId];
  return LOCATIONS[LEGACY_LOCATION_ID];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Weekday indices this location runs on, in rota order starting Sunday. The monthly
 * YOTE day is not a weekly day, so YOTE has none here — and it takes one Wednesday a
 * month from Nicola's, which the weekly label for Nicola's doesn't spell out.
 */
export function locationDays(id: BallancerLocationId): number[] {
  return ROTA.map((loc, i) => (loc === id ? i : -1)).filter((i) => i >= 0);
}

/**
 * Human label for a location's days — "Sunday – Wednesday" when they run together,
 * otherwise a comma list. Shown on the location picker so someone can tell at a
 * glance which site is even possible for them.
 */
export function locationDaysLabel(id: BallancerLocationId): string {
  if (id === "yote") return "4th Wednesday of the month";
  const days = locationDays(id);
  if (days.length === 0) return "Not currently running";
  if (days.length === 1) return DAY_NAMES[days[0]];
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  return contiguous
    ? `${DAY_NAMES[days[0]]} – ${DAY_NAMES[days[days.length - 1]]}`
    : days.map((d) => DAY_NAMES[d]).join(", ");
}

/**
 * Full street address for a location, from the environment.
 *
 * Env-only for the same reason fees and commission are (see app/lib/fees.ts and
 * app/lib/commission.ts): the value differs per environment and must never be
 * hardcoded. It also keeps Nicola's address out of the client bundle — Next.js does
 * not inline non-NEXT_PUBLIC_ vars, so this returns "" if ever called in a browser.
 *
 * `BALLANCER_ADDRESS` is honoured as a fallback for Stocks House so a deploy that
 * has not yet had the new vars added keeps sending a correct address rather than
 * an empty one.
 */
export function getLocationAddress(id: BallancerLocationId): string {
  const specific = process.env[LOCATIONS[id].addressEnvVar];
  if (specific) return specific;
  if (id === LEGACY_LOCATION_ID) return process.env.BALLANCER_ADDRESS ?? "";
  return "";
}

/**
 * Name plus address, for email and calendar-invite use. Falls back to the name alone.
 *
 * The name is NOT prepended when the address already begins with it. `BALLANCER_ADDRESS`
 * has always been the full "Stocks House, Scarisbrick, Southport PR8 5LF", so a naive
 * join renders "Stocks House, Stocks House, Scarisbrick, ..." in every confirmation email.
 * The guard matters beyond tidiness: the legacy var is still the fallback on any deploy
 * that has not had the new per-site vars added, so this cannot be fixed by editing one
 * .env and calling it done.
 */
export function getLocationDisplay(id: BallancerLocationId): { name: string; address: string; full: string } {
  const loc = LOCATIONS[id];
  const address = getLocationAddress(id).trim();
  if (!address) return { name: loc.name, address: "", full: loc.name };
  const alreadyNamed = address.toLowerCase().startsWith(loc.name.toLowerCase());
  return {
    name: loc.name,
    address,
    full: alreadyNamed ? address : `${loc.name}, ${address}`,
  };
}
